import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Mention, MentionsInput, SuggestionDataItem } from 'react-mentions';
import { useParams, useSearchParams } from 'react-router-dom';

import { DocumentSelectionDialog } from './document-selection-dialog';
import { DraftType, Jurisdiction } from '../../../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { DocIds } from '@/api/types/backend-dto-types';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { useDebounce } from '@/new/components/apideck/ApideckFilePickerCustom/utils/useDebounce';
import { Select, SelectItem, SelectValue, SelectContent, SelectTrigger } from '@/new/components/shadcn/select';
import { MentionsInputStyleKim } from '@/styles';
import { ExecuteKimSearch, KimMode } from '@/types';

const CLAIM_DOC_TYPES = [
  'witness statement',
  'deposition',
  'court document',
  'transcript',
  'pleadings',
  'affidavit',
  'expert report',
  'deposition transcript',
];

enum DraftTypeOption {
  Claim = DraftType.Claim,
  Rebuttal = DraftType.Rebuttal,
  Deposition = DraftType.Deposition,
  WitnessStatement = 'witness statement',
}

interface MentionInterface {
  id: string;
  display: string;
  childIndex: number;
  index: number;
  plainTextIndex: number;
}

interface QuestionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  executeSearch: ExecuteKimSearch;
  taggedDocuments: { doc_id: string; file_name: string }[];
  setTaggedDocuments: (v: { doc_id: string; file_name: string }[]) => void;
  mode: KimMode;
  draftType: DraftType | null;
  setDraftType: (draftType: DraftType | null) => void;
  jurisdiction: Jurisdiction[];
  isCreatingThread: boolean;
}

export function QuestionInput({
  value,
  onChange,
  placeholder,
  executeSearch,
  mode,
  taggedDocuments,
  setTaggedDocuments,
  draftType,
  setDraftType,
  jurisdiction,
  isCreatingThread,
}: QuestionInputProps) {
  const { t } = useTranslation();
  const { subsetId } = useParams();

  const [isFocused, setIsFocused] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const mentionsInputRef = useRef<any>(null);

  // Mention search state for async doc fetching
  const [mentionSearchQuery, setMentionSearchQuery] = useState<string>('');
  const debouncedMentionSearchQuery = useDebounce(mentionSearchQuery, 300);

  // Fetch documents with limit and search query (like fact-adder)
  const { data: docIdsResponse, isLoading: isLoadingDocs } = useGetDocIds(subsetId || null, {
    limit: 1000,
    searchQuery: debouncedMentionSearchQuery || undefined,
  });

  const docs = docIdsResponse?.documents ?? [];

  // Check search params
  const [searchParams] = useSearchParams();
  const selectedDoc = searchParams.get('selectedDocId');
  const processedDocRef = useRef<string | null>(null);

  const taggableDocs = useMemo(() => {
    if (draftType === DraftType.Deposition) {
      return docs.filter((doc) => doc.type && CLAIM_DOC_TYPES.includes(doc.type.toLowerCase()));
    }
    return docs;
  }, [draftType, docs]);

  const draftTypeOptions: { label: string; key: DraftTypeOption }[] = [
    { label: `${capitalizeFirstLetter(t('letter of claim'))} outline`, key: DraftTypeOption.Claim },
    { label: 'Letter of rebuttal outline', key: DraftTypeOption.Rebuttal },
    ...(jurisdiction.length === 0
      ? [
          { label: 'Deposition summary', key: DraftTypeOption.Deposition },
          { label: 'Witness statement summary', key: DraftTypeOption.WitnessStatement },
        ]
      : (jurisdiction
          .map((j) => {
            if (j === Jurisdiction.USFederal) {
              return { label: 'Deposition summary', key: DraftTypeOption.Deposition };
            }
            if (j === Jurisdiction.EnglandAndWales) {
              return { label: 'Witness statement summary', key: DraftTypeOption.WitnessStatement };
            }
          })
          .filter(Boolean) as { label: string; key: DraftTypeOption }[])),
  ];

  useEffect(() => {
    if (selectedDoc && selectedDoc !== processedDocRef.current) {
      const docName = docs.find((doc) => doc.id === selectedDoc)?.file.name;
      if (!docName) return;
      setTaggedDocuments([{ doc_id: selectedDoc, file_name: docName }]);
      onChange(`@[${docName}](${selectedDoc}) `);

      if (mentionsInputRef.current) {
        const textarea = mentionsInputRef.current;
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        textarea.focus();
      }

      processedDocRef.current = selectedDoc;
    }
  }, [selectedDoc, docs, setTaggedDocuments, onChange]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) {
        executeSearch({ question: value, questionMode: mode, draftType });
      }
    }
  };

  const renderTruncatedMention = (id: string, display: string) => {
    return display.length > 20 ? `${display.substring(0, 20)}...` : display;
  };

  const fetchMentionSuggestions = useCallback(
    (search: string, callback: (data: SuggestionDataItem[]) => void) => {
      // Update search query (will be debounced and trigger new fetch)
      setMentionSearchQuery(search);
      // Return current results - component will re-render when new data arrives
      const suggestions = taggableDocs.map((doc) => ({
        id: doc.id,
        display: sanitizeFileName(doc.file.name),
      }));
      callback(suggestions);
    },
    [taggableDocs],
  );

  const handleChange = ({ newValue, mentions }: { newValue: string; mentions: MentionInterface[] }) => {
    const uniqueTaggedDocs = mentions.reduce(
      (acc, mention) => {
        if (!acc.some((doc) => doc.doc_id === mention.id)) {
          acc.push({ doc_id: mention.id, file_name: mention.display });
        }
        return acc;
      },
      [] as { doc_id: string; file_name: string }[],
    );

    setTaggedDocuments(uniqueTaggedDocs);
    onChange(newValue);
  };

  const triggerMentions = useCallback(() => {
    if (mentionsInputRef.current) {
      const textarea = mentionsInputRef.current;
      const currentValue = value;
      const cursorPosition = textarea.selectionStart;

      // Insert '@' at the cursor position
      const newValue = currentValue + '@';
      onChange(newValue);

      // Use requestAnimationFrame to ensure the DOM has updated
      requestAnimationFrame(() => {
        // Set cursor position after the inserted '@'
        textarea.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
        textarea.focus();

        // Simulate typing '@' by dispatching events
        const inputEvent = new InputEvent('input', { bubbles: true });
        textarea.dispatchEvent(inputEvent);

        const keydownEvent = new KeyboardEvent('keydown', { key: '@', bubbles: true });
        textarea.dispatchEvent(keydownEvent);
      });
    }
  }, [onChange, value]);

  const sanitizeFileName = (fileName: string) => {
    return fileName.replace(/[[]()]/g, '');
  };

  const handleDraftTypeChange = (value: string) => {
    if (value === DraftTypeOption.WitnessStatement || value === DraftTypeOption.Deposition) {
      // Prefill the question and show document selection modal
      onChange('Create a deposition summary for...');
      setShowDocumentModal(true);
      setDraftType(DraftType.Deposition);
    } else {
      setDraftType(value as DraftType);
    }
  };

  const handleDocumentSelection = () => {
    setDraftType(DraftType.Deposition);
    setShowDocumentModal(false);
  };

  const toggleDocumentSelection = (doc: DocIds) => {
    const isAlreadyTagged = taggedDocuments.some((taggedDoc) => taggedDoc.doc_id === doc.id);

    if (isAlreadyTagged) {
      // Remove the document mention from the input
      const mentionToRemove = `@[${sanitizeFileName(doc.file.name)}](${doc.id})`;
      const newValue = value.replace(mentionToRemove, '').trim();
      onChange(newValue);

      // Update tagged documents
      const updatedTaggedDocs = taggedDocuments.filter((taggedDoc) => taggedDoc.doc_id !== doc.id);
      setTaggedDocuments(updatedTaggedDocs);
    } else {
      // Add the document mention to the input
      const mentionToAdd = `@[${sanitizeFileName(doc.file.name)}](${doc.id})`;
      const currentValue = value || 'Create a deposition summary for...';
      const newValue = currentValue.replace('...', `... ${mentionToAdd}`);
      onChange(newValue);

      // Update tagged documents
      const updatedTaggedDocs = [...taggedDocuments, { doc_id: doc.id, file_name: doc.file.name }];
      setTaggedDocuments(updatedTaggedDocs);
    }
  };

  return (
    <div className="relative flex w-full flex-col items-center gap-4">
      <div
        className={`flex w-full flex-col gap-1 rounded-md border px-4 py-4 transition-all duration-300 ${
          isFocused ? 'border-gray-300' : 'border-gray-200'
        }`}
      >
        <div className="relative min-h-20">
          <div className="max-h-56">
            <MentionsInput
              value={value}
              onChange={(e, newValue, newPlainTextValue, mentions) => handleChange({ newValue, mentions })}
              placeholder={placeholder}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              a11ySuggestionsListLabel={'Suggested mentions'}
              onKeyDown={onKeyDown}
              inputRef={mentionsInputRef}
              style={MentionsInputStyleKim}
            >
              <Mention
                trigger="@"
                data={fetchMentionSuggestions}
                appendSpaceOnAdd={true}
                displayTransform={renderTruncatedMention}
                isLoading={isLoadingDocs}
                style={{
                  backgroundColor: '#EEEEEE',
                  borderRadius: '2px',
                }}
                onAdd={() => {
                  setMentionSearchQuery('');
                  trackCustomEvent(TrackingEventsV2.KimTagDocument);
                }}
              />
            </MentionsInput>
          </div>
          {mode === KimMode.DraftFlow && (
            <div className="my-2 w-fit">
              <Select onValueChange={handleDraftTypeChange}>
                <SelectTrigger className="text-xs">
                  <SelectValue placeholder="Draft type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {draftTypeOptions.map((option) => (
                    <SelectItem key={option.key} value={option.key}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex items-end justify-between gap-2 pr-1">
          <button
            disabled={isCreatingThread}
            className="flex cursor-pointer items-center text-xs text-gray-500 italic hover:text-gray-700"
            onClick={triggerMentions}
          >
            @ Documents
          </button>
          <button
            disabled={isCreatingThread}
            className="bg-brandSecondary hover:bg-brandSecondary-hover flex h-7 w-7 cursor-pointer items-center justify-center rounded-full p-1 text-sm font-semibold text-white"
            onClick={() => executeSearch({ question: value, questionMode: mode, draftType })}
          >
            <span
              className={`iconify ${isCreatingThread ? 'lucide--loader-circle animate-spin' : 'lucide--arrow-right'} h-5 w-5 text-white`}
            ></span>
          </button>
        </div>
      </div>

      {/* Document Selection Dialog */}
      <DocumentSelectionDialog
        isOpen={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        subsetId={subsetId ?? ''}
        taggedDocuments={taggedDocuments}
        onToggleDocument={toggleDocumentSelection}
        onConfirm={handleDocumentSelection}
        onCancel={() => {
          setTaggedDocuments([]);
          onChange('');
          setShowDocumentModal(false);
        }}
        claimDocTypes={CLAIM_DOC_TYPES}
      />
    </div>
  );
}
